import React, { useState, useEffect } from 'react';
import { fetchWrapper } from '../utils/fetchWrapper';
import { toast } from 'react-hot-toast';
import {
  MdPeople,
  MdFilterList,
  MdClose,
  MdInbox,
  MdStar,
  MdCheckCircle,
  MdCancel,
  MdVisibility,
  MdBlock,
  MdChevronLeft,
  MdChevronRight,
  MdLock,
  MdFileDownload,
  MdDeleteForever,
  MdInfoOutline,
  MdPrint
} from 'react-icons/md';
import './Leads.css';
import SearchableSelect from './ui/SearchableSelect';
import './ui/SearchableSelect.css';
import ConfirmModal from './ui/ConfirmModal';
import { formatDate } from '../utils/units';

function Leads() {
  const [projects, setProjects] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1, limit: 50 });
  const [expandedRow, setExpandedRow] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);

  // Confirm modal state
  const [confirmModal, setConfirmModal] = useState({
    show: false,
    title: '',
    message: '',
    detail: '',
    confirmText: 'Confirm',
    variant: 'danger',
    confirmPhrase: '',
    onConfirm: null,
    loading: false,
  });

  // Filters
  const [filters, setFilters] = useState({
    projectDomain: '',
    startDate: '',
    endDate: '',
    search: '',
  });

  // Load all projects for the dropdown
  const loadProjects = async () => {
    try {
      const res = await fetchWrapper.get('/projects');
      setProjects(res.data || []);
    } catch (err) {
      console.error('Failed to load projects', err);
    }
  };

  // Load leads with filters
  const loadLeads = async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page,
        limit: 50,
        ...(filters.projectDomain && { projectDomain: filters.projectDomain }),
        ...(filters.startDate && { startDate: filters.startDate }),
        ...(filters.endDate && { endDate: filters.endDate }),
        ...(filters.search && { search: filters.search }),
      });

      const res = await fetchWrapper.get(`/leads?${params}`);
      setLeads(res.data.data || []);
      setPagination(res.data.pagination);
      setSelectedIds([]);
    } catch (err) {
      console.error('Failed to fetch leads', err);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadProjects();
    loadLeads();
  }, []);

  // Apply filters
  const handleFilterChange = (e) => {
    setFilters((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const applyFilters = () => {
    loadLeads(1); // Reset to page 1 when filters change
  };

  const clearFilters = () => {
    setFilters({
      projectDomain: '',
      startDate: '',
      endDate: '',
      search: '',
    });
    setTimeout(() => loadLeads(1), 100);
  };

  // Export leads (current filters) to CSV
  const exportLeads = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({
        ...(filters.projectDomain && { projectDomain: filters.projectDomain }),
        ...(filters.startDate && { startDate: filters.startDate }),
        ...(filters.endDate && { endDate: filters.endDate }),
        ...(filters.search && { search: filters.search }),
      });

      const res = await fetchWrapper.get(`/leads/export?${params}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `leads-export-${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Failed to export leads: ' + (err.response?.data?.message || err.message));
    }
    setExporting(false);
  };

  // Print leads (current filters) — opens a clean printable view with native print preview
  const printLeads = async () => {
    setPrinting(true);
    try {
      const params = new URLSearchParams({
        ...(filters.projectDomain && { projectDomain: filters.projectDomain }),
        ...(filters.startDate && { startDate: filters.startDate }),
        ...(filters.endDate && { endDate: filters.endDate }),
        ...(filters.search && { search: filters.search }),
      });

      const res = await fetchWrapper.get(`/leads/print?${params}`);
      const printLeadsData = res.data.data || [];

      const esc = (val) => {
        const str = val === undefined || val === null ? '' : String(val);
        return str
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      };

      const rowsHtml = printLeadsData.map((lead) => {
        return `
          <tr>
            <td>${esc(formatDate(lead.createdAt, "dd-mm-yyyy"))} ${esc(formatDate(lead.createdAt, "time-only"))}</td>
            <td>${esc(lead.name)}</td>
            <td>${esc(lead.phone)}</td>
            <td>${esc(lead.projectDomain)}</td>
            <td>${esc(lead.ip || 'N/A')}</td>
            <td>${esc([lead.geo?.city, lead.geo?.state, lead.geo?.country].filter(Boolean).join(', ') || '-')}</td>
            <td>${lead.emailSent ? 'Sent' : 'Failed'}</td>
          </tr>
        `;
      }).join('');

      const filterSummary = [
        filters.projectDomain && `Project: ${esc(filters.projectDomain)}`,
        filters.startDate && `From: ${esc(filters.startDate)}`,
        filters.endDate && `To: ${esc(filters.endDate)}`,
        filters.search && `Search: ${esc(filters.search)}`,
      ].filter(Boolean).join(' &nbsp;|&nbsp; ') || 'All Leads';

      const html = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Leads Report</title>
          <meta charset="utf-8" />
          <style>
            * { box-sizing: border-box; }
            body { font-family: Arial, Helvetica, sans-serif; margin: 24px; color: #1e293b; }
            h1 { font-size: 20px; margin-bottom: 4px; }
            .meta { font-size: 12px; color: #64748b; margin-bottom: 16px; }
            table { width: 100%; border-collapse: collapse; font-size: 12px; }
            th, td { border: 1px solid #cbd5e1; padding: 6px 8px; text-align: left; }
            th { background: #1f2937; color: #fff; text-transform: uppercase; font-size: 10px; letter-spacing: 0.5px; }
            tr:nth-child(even) { background: #f8fafc; }
            @media print {
              @page { size: landscape; margin: 12mm; }
            }
          </style>
        </head>
        <body>
          <h1>Leads Report</h1>
          <div class="meta">
            ${filterSummary} &nbsp;|&nbsp; Total: ${printLeadsData.length} &nbsp;|&nbsp; Generated: ${esc(formatDate(new Date().toISOString(), "dd-mm-yyyy-time"))} IST
          </div>
          <table>
            <thead>
              <tr>
                <th>Date &amp; Time</th>
                <th>Name</th>
                <th>Phone</th>
                <th>Project</th>
                <th>IP Address</th>
                <th>Location</th>
                <th>Email Status</th>
              </tr>
            </thead>
            <tbody>
              ${rowsHtml}
            </tbody>
          </table>
        </body>
        </html>
      `;

      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        toast.error('Please allow pop-ups to print leads.');
        setPrinting(false);
        return;
      }
      printWindow.document.open();
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
      };
    } catch (err) {
      toast.error('Failed to prepare print view: ' + (err.response?.data?.message || err.message));
    }
    setPrinting(false);
  };

  // Block IP — show confirmation modal
  const blockIP = (ip, permanent = false) => {
    const typeLabel = permanent ? 'permanently' : 'temporarily (7 days)';
    setConfirmModal({
      show: true,
      title: permanent ? 'Permanently Block IP' : 'Temporarily Block IP',
      message: `Are you sure you want to block ${ip} ${typeLabel}?`,
      detail: permanent 
        ? 'This IP will be permanently blocked from accessing the entire server and must be manually unblocked.' 
        : 'This IP will be blocked for 7 days. It will automatically regain access after the period expires.',
      confirmText: permanent ? 'Block Permanently' : 'Block (7 Days)',
      variant: 'danger',
      confirmPhrase: 'confirm',
      loading: false,
      onConfirm: () => executeBlockIP(ip, permanent),
    });
  };

  const executeBlockIP = async (ip, permanent) => {
    setConfirmModal(prev => ({ ...prev, loading: true }));
    try {
      await fetchWrapper.post('/leads/block-ip', { 
        ip, 
        reason: permanent ? 'Manual permanent block' : 'Fake/Spam lead',
        permanent 
      });
      toast.success(`IP blocked ${permanent ? 'permanently' : 'temporarily'}!`);
      setConfirmModal(prev => ({ ...prev, show: false, loading: false }));
      loadLeads(pagination.page);
    } catch (err) {
      toast.error('Failed to block IP: ' + (err.response?.data?.message || err.message));
      setConfirmModal(prev => ({ ...prev, loading: false }));
    }
  };

  // Delete lead — show confirmation modal
  const deleteLead = (lead) => {
    setConfirmModal({
      show: true,
      title: 'Permanently Delete Lead',
      message: `Are you sure you want to delete the lead for "${lead.name}" (${lead.phone})?`,
      detail: `This will permanently remove the lead record — name, phone, project (${lead.projectDomain}), IP (${lead.ip || 'N/A'}), and location data. This action cannot be undone. A record of who deleted it and when will be kept in the audit log.`,
      confirmText: 'Delete Permanently',
      variant: 'danger',
      confirmPhrase: 'delete',
      loading: false,
      onConfirm: () => executeDeleteLead(lead._id),
    });
  };

  const executeDeleteLead = async (id) => {
    setConfirmModal(prev => ({ ...prev, loading: true }));
    try {
      await fetchWrapper.delete(`/leads/${id}`);
      toast.success('Lead deleted successfully!');
      setConfirmModal(prev => ({ ...prev, show: false, loading: false }));
      setExpandedRow(null);
      loadLeads(pagination.page);
    } catch (err) {
      toast.error('Failed to delete lead: ' + (err.response?.data?.message || err.message));
      setConfirmModal(prev => ({ ...prev, loading: false }));
    }
  };

  // Selection helpers
  const toggleSelectLead = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === leads.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(leads.map((l) => l._id));
    }
  };

  // Bulk delete — show confirmation modal
  const bulkDeleteLeads = () => {
    const count = selectedIds.length;
    setConfirmModal({
      show: true,
      title: `Permanently Delete ${count} Lead${count > 1 ? 's' : ''}`,
      message: `Are you sure you want to delete ${count} selected lead${count > 1 ? 's' : ''}?`,
      detail: 'This will permanently remove all selected lead records — name, phone, project, IP, and location data. This action cannot be undone. A record of who deleted them and when will be kept in the audit log.',
      confirmText: `Delete ${count} Lead${count > 1 ? 's' : ''}`,
      variant: 'danger',
      confirmPhrase: 'delete',
      loading: false,
      onConfirm: executeBulkDelete,
    });
  };

  const executeBulkDelete = async () => {
    setConfirmModal(prev => ({ ...prev, loading: true }));
    try {
      const res = await fetchWrapper.delete('/leads/bulk-delete', { data: { ids: selectedIds } });
      toast.success(res.data.message || 'Leads deleted successfully!');
      setConfirmModal(prev => ({ ...prev, show: false, loading: false }));
      setExpandedRow(null);
      setSelectedIds([]);
      loadLeads(pagination.page);
    } catch (err) {
      toast.error('Failed to delete leads: ' + (err.response?.data?.message || err.message));
      setConfirmModal(prev => ({ ...prev, loading: false }));
    }
  };

  const closeConfirmModal = () => {
    setConfirmModal(prev => ({ ...prev, show: false, loading: false }));
  };

  // Pagination
  const goToPage = (page) => {
    if (page < 1 || page > pagination.pages) return;
    loadLeads(page);
  };

  return (
    <div className="leads-container">
      <div className="leads-header">
        <div>
          <h3 className="leads-title">Leads Management</h3>
          <p className="text-muted mb-0">View and manage all lead submissions</p>
        </div>
        <div className="d-flex gap-2 align-items-center">
          <span className="badge-custom badge-primary-soft">
            <MdPeople className="me-1" /> {pagination.total} Total Leads
          </span>
          <button
            className="btn btn-outline-secondary d-flex align-items-center gap-1"
            onClick={exportLeads}
            disabled={exporting}
          >
            <MdFileDownload /> {exporting ? 'Exporting...' : 'Export Excel'}
          </button>
          <button
            className="btn btn-outline-secondary d-flex align-items-center gap-1"
            onClick={printLeads}
            disabled={printing}
          >
            <MdPrint /> {printing ? 'Preparing...' : 'Print'}
          </button>
          {selectedIds.length > 0 && (
            <button
              className="btn btn-danger d-flex align-items-center gap-1"
              onClick={bulkDeleteLeads}
            >
              <MdDeleteForever /> Delete Selected ({selectedIds.length})
            </button>
          )}
        </div>
      </div>

      {/* Filters Section */}
      <div className="filters-card">
        <div className="card-header">
          <div className="filters-title">
            <MdFilterList className="me-2" /> Filter Leads
          </div>
          <div className="d-flex gap-2">
            <button onClick={applyFilters} className="btn btn-dark">
              Apply
            </button>
            <button onClick={clearFilters} className="btn btn-outline-secondary" style={{ minWidth: '42px' }}>
              <MdClose />
            </button>
          </div>
        </div>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label">Project</label>
              <SearchableSelect
                value={filters.projectDomain}
                onChange={(val) => setFilters(prev => ({ ...prev, projectDomain: val }))}
                options={[
                  { value: "", label: "All Projects" },
                  ...projects.map(p => ({ value: p.domain, label: p.projectName || p.domain }))
                ]}
                placeholder="All Projects"
                searchPlaceholder="Search projects..."
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Start Date</label>
              <input
                type="date"
                className="form-control"
                name="startDate"
                value={filters.startDate}
                onChange={handleFilterChange}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">End Date</label>
              <input
                type="date"
                className="form-control"
                name="endDate"
                value={filters.endDate}
                onChange={handleFilterChange}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Search</label>
              <input
                type="text"
                className="form-control"
                name="search"
                placeholder="Name, Phone, IP..."
                value={filters.search}
                onChange={handleFilterChange}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Leads Table */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <div className="leads-table-card">
          <div className="table-responsive">
            <table className="table-custom">
              <thead>
                <tr>
                  <th className="checkbox-col">
                    <input
                      type="checkbox"
                      checked={leads.length > 0 && selectedIds.length === leads.length}
                      onChange={toggleSelectAll}
                      title="Select all"
                    />
                  </th>
                  <th>Date & Time</th>
                  <th>Name</th>
                  <th>Phone</th>
                  <th>Project</th>
                  <th>IP Address</th>
                  <th>Location</th>
                  <th>Email Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {leads.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="text-center py-5 text-muted">
                      <MdInbox size={40} className="d-block mx-auto mb-2" />
                      No leads found matching your criteria
                    </td>
                  </tr>
                ) : (
                  leads.map((lead, index) => (
                    <React.Fragment key={lead._id}>
                      <tr className={selectedIds.includes(lead._id) ? 'row-selected' : ''}>
                        <td className="checkbox-col">
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(lead._id)}
                            onChange={() => toggleSelectLead(lead._id)}
                          />
                        </td>
                        <td data-label="Date">
                          <div className="fw-medium">{formatDate(lead.createdAt, "dd-mm-yyyy")}</div>
                          <div className="small text-muted">{formatDate(lead.createdAt, "time-only")}</div>
                        </td>
                        <td data-label="Name">
                          <div className="lead-name-link">{lead.name}</div>
                          {lead.interest && (
                            <div className="lead-interest">
                              <MdStar className="text-warning me-1" size={12} /> {lead.interest}
                            </div>
                          )}
                        </td>
                        <td data-label="Phone">
                          <a href={`tel:${lead.phone}`} className="text-decoration-none text-dark fw-medium">
                            {lead.phone}
                          </a>
                        </td>
                        <td data-label="Project">
                          <span className="project-badge">
                            {lead.projectDomain}
                          </span>
                        </td>
                        <td data-label="IP">
                          <code className="text-muted">{lead.ip || 'N/A'}</code>
                        </td>
                        <td data-label="Location">
                          {lead.geo && lead.geo.city ? (
                            <div className="small">
                              <div>{lead.geo.city}, {lead.geo.state}</div>
                              <div className="text-muted">{lead.geo.country}</div>
                            </div>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td data-label="Email Status">
                          <div className="d-flex align-items-center gap-1">
                            {lead.emailSent ? (
                              <span className="badge-custom badge-success-soft">
                                <MdCheckCircle className="me-1" /> Sent
                              </span>
                            ) : (
                              <span className="badge-custom badge-danger-soft">
                                <MdCancel className="me-1" /> Failed
                              </span>
                            )}
                            {lead.emailSent && (
                              <span className="email-info-icon">
                                <MdInfoOutline size={16} />
                                <span className="email-info-tooltip">
                                  <div className="fw-medium mb-1">
                                    {lead.emailSentAt ? formatDate(lead.emailSentAt, "dd-mm-yyyy-time") : 'N/A'}
                                  </div>
                                  {lead.emailsSentTo && lead.emailsSentTo.length > 0 ? (
                                    lead.emailsSentTo.map((email, idx) => (
                                      <div key={idx}>{email}</div>
                                    ))
                                  ) : (
                                    <div className="text-muted">No recipients recorded</div>
                                  )}
                                </span>
                              </span>
                            )}
                          </div>
                        </td>
                        <td data-label="Actions">
                          <div className="action-group">
                            <button
                              className={`btn-action-square btn-action-view ${expandedRow === index ? 'active' : ''}`}
                              type="button"
                              onClick={() => setExpandedRow(expandedRow === index ? null : index)}
                              title="View Details"
                            >
                              <MdVisibility />
                            </button>
                            {lead.ip && (
                              <div className="d-flex gap-1">
                                <button
                                  className="btn-action-square btn-action-danger"
                                  onClick={() => blockIP(lead.ip, false)}
                                  title="Block IP (Temporary - 7 days)"
                                >
                                  <MdBlock />
                                </button>
                                <button
                                  className="btn-action-square btn-action-dark"
                                  onClick={() => blockIP(lead.ip, true)}
                                  title="Block IP Permanently"
                                >
                                  <MdLock />
                                </button>
                              </div>
                            )}
                            <button
                              className="btn-action-square btn-action-danger"
                              onClick={() => deleteLead(lead)}
                              title="Delete Lead Permanently"
                            >
                              <MdDeleteForever />
                            </button>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td colSpan="9" className="p-0 border-0">
                          {expandedRow === index && (
                            <div className="technical-details-card">
                              <div className="row g-4">
                                <div className="col-lg-7 col-md-12">
                                  <h6 className="tech-section-title">Technical Details</h6>
                                  <div className="detail-row">
                                    <span className="detail-label">Lead ID</span>
                                    <span className="detail-value mono">{lead._id}</span>
                                  </div>
                                  <div className="detail-row">
                                    <span className="detail-label">User Agent</span>
                                    <span className="detail-value">{lead.userAgent || 'N/A'}</span>
                                  </div>
                                  {lead.emailSentAt && (
                                    <div className="detail-row">
                                      <span className="detail-label">Email Sent</span>
                                      <span className="detail-value">{formatDate(lead.emailSentAt, "dd-mm-yyyy-time")}</span>
                                    </div>
                                  )}
                                </div>
                                {lead.emailsSentTo && lead.emailsSentTo.length > 0 && (
                                  <div className="col-lg-5 col-md-12">
                                    <h6 className="tech-section-title">Email Recipients</h6>
                                    <div className="d-flex flex-wrap gap-2">
                                      {lead.emailsSentTo.map((email, idx) => (
                                        <span key={idx} className="badge bg-white text-dark border px-2 py-1 shadow-sm">{email}</span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </td>
                      </tr>
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="pagination-container">
              <ul className="pagination pagination-sm mb-0">
                <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => goToPage(pagination.page - 1)}>
                    <MdChevronLeft />
                  </button>
                </li>
                {[...Array(pagination.pages)].map((_, i) => {
                  const page = i + 1;
                  if (
                    page === 1 ||
                    page === pagination.pages ||
                    (page >= pagination.page - 2 && page <= pagination.page + 2)
                  ) {
                    return (
                      <li key={page} className={`page-item ${pagination.page === page ? 'active' : ''}`}>
                        <button className="page-link" onClick={() => goToPage(page)}>
                          {page}
                        </button>
                      </li>
                    );
                  } else if (page === pagination.page - 3 || page === pagination.page + 3) {
                    return (
                      <li key={page} className="page-item disabled">
                        <span className="page-link">...</span>
                      </li>
                    );
                  }
                  return null;
                })}
                <li className={`page-item ${pagination.page === pagination.pages ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => goToPage(pagination.page + 1)}>
                    <MdChevronRight />
                  </button>
                </li>
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmModal
        show={confirmModal.show}
        title={confirmModal.title}
        message={confirmModal.message}
        detail={confirmModal.detail}
        confirmText={confirmModal.confirmText}
        variant={confirmModal.variant}
        confirmPhrase={confirmModal.confirmPhrase}
        loading={confirmModal.loading}
        onConfirm={confirmModal.onConfirm}
        onCancel={closeConfirmModal}
      />
    </div>
  );
}

export default Leads;
